<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="instrument-instance-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row class="pr-3">
          <b-col v-if="filter_display.show_type_filter" class="pr-0">
            <select-filter
              variant="info"
              :icon="instrument.icon"
              placeholder="All Instruments"
              :collection="instrument.filters"
              value-key="id"
              text-key="name"
              filter-field="instrument_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('instrument', $event)"
            />
          </b-col>
          <b-col v-if="filter_display.show_sample_type_filter" class="pr-0">
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="instrument.instrument_tests.analysis.analyte.sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('sample_type', $event)"
            />
          </b-col>
          <b-col v-if="filter_display.show_analyte_filter" class="pr-0">
            <select-filter
              variant="info"
              :icon="analyte.icon"
              placeholder="All Analytes"
              :collection="analyte.filters"
              value-key="id"
              text-key="name"
              filter-field="instrument.instrument_tests.analysis.analyte_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('analyte', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="name" slot-scope="{ row }">
        <a :href="`#/instrument-instances/${row.id}`" class="btn-link">
          {{ row.name }}
        </a>
      </span>
      <span slot="identifier" slot-scope="{ row }">
        <a :href="`#/instrument-instances/${row.id}`" class="btn-link">
          {{ row.identifier }}
        </a>
      </span>
      <span slot="instrument.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['instrument']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/instrument/${getData(row, {
                levels: ['instrument'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["instrument"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="priority" slot-scope="{ row }">
        {{ formatPriority(row) }}
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit instrument instance')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/instrument-instances/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete instrument instance')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import SelectFilter from "@/components/shared/SelectFilter";
import InstrumentInstanceCollection from "@/collections/lab/InstrumentInstanceCollection";
import AnalyteModel from "@/models/lab/AnalyteModel";
import InstrumentModel from "@/models/lab/InstrumentModel";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "InstrumentInstanceServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ModalTrashBasket,
    ServerTable,
    SelectFilter,
  },
  props: {
    collection: {
      type: Object,
      default: () => new InstrumentInstanceCollection(),
    },
    instruments: {
      type: Object,
      default: () => null,
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    analytes: {
      type: Object,
      default: () => null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "equipment_number",
        "name",
        "instrument.name",
        "identifier",
        "priority",
        "location",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => ["name", "instrument.name"],
    },
    filter_display: {
      type: Object,
      default: () => {
        return {
          show_type_filter: true,
          show_sample_type_filter: true,
          show_analyte_filter: true,
        };
      },
    },
    task_type: {
      type: String,
      default: null,
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit instrument instance") ||
      this.checkPermission("delete instrument instance");

    return {
      entity_type: "instrumentInstance",
      instrument_id: this.$route.params.instrument_id || null,
      instrument_collection: null,
      sample_type_collection: null,
      analyte_collection: null,
      laboratory_id: this.$route.params.laboratory_id || null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            equipment_number: "EQP#",
            "instrument.name": "Instrument",
          },
          sortable: this.sortable,
          filterable: this.filterable,
        },
      }),
    };
  },
  computed: {
    ...mapGetters({
      trash: "instrumentInstanceTrash",
    }),
    analyte() {
      return {
        icon: `fa-${AnalyteModel.iconStatic()}`,
        filters: this.analytes || this.analyte_collection,
      };
    },
    instrument() {
      return {
        icon: `fa-${InstrumentModel.iconStatic()}`,
        filters: this.instruments || this.instrument_collection,
      };
    },
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    show_modal() {
      return (
        this.checkPermission("delete instrument instance") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    prepareCollectionFilter(filter) {
      this.instrument_id &&
        filter.filterEquals("instrument_id", this.instrument_id);
      this.laboratory_id &&
        filter.filterEquals("laboratory_id", this.laboratory_id);
      this.task_type &&
        filter.filterEquals(
          `${this.task_type}_task_instrument_instances.${this.task_type}_task_id`,
          this.$route.params[`${this.task_type}_task_id`]
        );

      return filter;
    },
    formatPriority(row) {
      return row.primary ? "Primary" : row.backup ? "Backup" : "";
    },
    async init() {
      const payload = {
        message: this.$bvToast,
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
      };

      !this.instruments &&
        (this.instrument_collection = await getCollection({
          ...payload,
          collection_type: "instrument",
        }));
      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
        }));
      !this.analytes &&
        (this.analyte_collection = await getCollection({
          ...payload,
          collection_type: "analyte",
        }));
    },
  },
  created() {
    this.init();
  },
};
</script>
